"use client"

// Conversation: a scroll viewport that keeps the latest content in view while
// the agent streams, plus a "jump to latest" button when the user scrolls up.
//
// Adapted from Vercel AI Elements (https://ai-sdk.dev/elements, conversation),
// re-authored to be token-driven and dependency-free: AI Elements' original
// pulls `use-stick-to-bottom` and assumes the AI-SDK transport. We only need the
// presentation, so the stick-to-bottom behavior is implemented inline.

import { cn } from "@/lib/utils"
import { ArrowDown } from "lucide-react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react"

interface ConversationContextValue {
  /** The scroll viewport element, so the scroll button can drive it. */
  viewportRef: React.RefObject<HTMLDivElement | null>
  /** True when the viewport is scrolled to (near) the bottom. */
  atBottom: boolean
  /** Smooth-scroll the viewport to the bottom (an explicit user action). */
  scrollToBottom: () => void
}

const ConversationContext = createContext<ConversationContextValue | null>(null)

function useConversation(): ConversationContextValue {
  const ctx = useContext(ConversationContext)
  // Fail loud: a misplaced sub-component is a developer error, not a runtime
  // condition to paper over with a default.
  if (!ctx) {
    throw new Error("Conversation sub-components must be rendered inside <Conversation>.")
  }
  return ctx
}

/**
 * Scroll viewport for a conversation thread. Opens pinned to the latest message
 * and auto-follows new content unless the user has scrolled up.
 *
 * @param conversationKey - identifies the open conversation (the session id).
 *   When it changes, the viewport jumps to the bottom instantly so a reopened
 *   transcript starts at the latest message instead of the top.
 * @param children - the thread content (use `ConversationContent`) and an
 *   optional `ConversationScrollButton`.
 * @param className - extra classes merged onto the viewport.
 */
export function Conversation({
  conversationKey,
  className,
  children,
  ...props
}: ComponentProps<"div"> & { conversationKey?: string }): ReactNode {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [atBottom, setAtBottom] = useState<boolean>(true)
  // Mirror `atBottom` into a ref so the ResizeObserver callback reads the
  // current value without being re-created (and re-observing) on every change.
  const atBottomRef = useRef<boolean>(true)
  atBottomRef.current = atBottom

  // Jump straight to the bottom with no animation. A smooth scroll would be
  // outrun by content that lays out (or images/charts that load) after it
  // starts, leaving a long transcript stranded above the last message; an
  // instant jump lands on whatever the current height is, and the
  // ResizeObserver below re-pins as later content grows the viewport.
  const jumpToBottom = useCallback(() => {
    const el = viewportRef.current
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }, [])

  const scrollToBottom = useCallback(() => {
    const el = viewportRef.current
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
    }
  }, [])

  const handleScroll = useCallback(() => {
    const el = viewportRef.current
    if (!el) {
      return
    }
    // 40px slack so tiny rounding differences still count as "at bottom".
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    setAtBottom(distanceFromBottom < 40)
  }, [])

  // Opening a conversation: land at the latest message before the browser
  // paints (useLayoutEffect), so the user never sees the thread start at the
  // top and scroll down. Re-runs when the conversation id changes.
  useLayoutEffect(() => {
    setAtBottom(true)
    atBottomRef.current = true
    jumpToBottom()
  }, [conversationKey, jumpToBottom])

  // Keep the view pinned to the bottom as the content grows — new streamed
  // tokens, and async content (charts/images) that only gets its height after
  // it loads. Only follows while the user is already at the bottom, so we never
  // yank them away from earlier content they scrolled up to read. Instant, for
  // the same reason jumpToBottom is.
  useEffect(() => {
    const el = viewportRef.current
    if (!el || typeof ResizeObserver === "undefined") {
      return
    }
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current) {
        el.scrollTop = el.scrollHeight
      }
    })
    // Observe the content wrapper (first child), whose height reflects the
    // messages; the viewport itself has a fixed height.
    const content = el.firstElementChild
    if (content) {
      observer.observe(content)
    }
    return () => observer.disconnect()
  }, [])

  return (
    <ConversationContext.Provider value={{ viewportRef, atBottom, scrollToBottom }}>
      <div
        ref={viewportRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        className={cn("scrollbar-thin relative mx-auto w-full max-w-4xl flex-1 overflow-y-auto", className)}
        {...props}
      >
        {children}
      </div>
    </ConversationContext.Provider>
  )
}

/**
 * Padded content wrapper for the conversation thread.
 *
 * @param className - extra classes merged onto the content container.
 */
export function ConversationContent({ className, ...props }: ComponentProps<"div">): ReactNode {
  return (
    <div className={cn("w-full space-y-6 px-4 py-6", className)} {...props} />
  )
}

/**
 * A floating control that scrolls the conversation to the latest message.
 * Renders only when the viewport is scrolled away from the bottom.
 *
 * @param label - accessible Spanish label for the button.
 * @param className - extra classes merged onto the button.
 */
export function ConversationScrollButton({
  label = "Ir al mensaje más reciente",
  className,
  ...props
}: ComponentProps<"button"> & { label?: string }): ReactNode {
  const { atBottom, scrollToBottom } = useConversation()
  if (atBottom) {
    return null
  }
  return (
    <button
      type="button"
      onClick={scrollToBottom}
      aria-label={label}
      title={label}
      className={cn(
        "sticky bottom-4 left-1/2 z-10 ml-auto mr-4 flex size-9 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-md transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        className
      )}
      {...props}
    >
      <ArrowDown className="size-4" aria-hidden="true" />
    </button>
  )
}
